import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Check, X, Baby,
  Stethoscope, Apple, MessageCircle, Salad, Heart,
  Users, Calendar, ShieldCheck, PhoneCall, Award,
  Star, Sparkles, TrendingUp, Target
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GST_RATE = 0.18;
const PRICE = 85000;

function formatPrice(amount) { return amount.toLocaleString("en-IN"); }
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
const stagger = (i) => ({ ...fadeUp, transition: { duration: 0.6, delay: i * 0.1 } });

const SectionLabel = ({ children, light }) => (
  <span className={`text-[11px] tracking-[0.25em] uppercase font-semibold ${light ? "text-[#D4AF37]" : "text-[#D4AF37]"}`}>{children}</span>
);

const JOURNEY = [
  {
    phase: "01", title: "Preconception", color: "#E8B4B8",
    description: "Personalized fertility nutrition, cycle optimization, and biomarker-guided planning to prepare your body for conception.",
    details: "Your dietitian conducts a comprehensive assessment and works with specialists to create a nutrition plan tailored to your hormonal profile, lifestyle, and fertility goals."
  },
  {
    phase: "02", title: "Pregnancy", color: "#A8D5BA",
    description: "Trimester-specific nutrition, prenatal care coordination, and ongoing doctor consultations as your body changes.",
    details: "Each trimester brings different nutritional needs. We adjust your plan in real-time, coordinate with your OB-GYN, and provide weekly monitoring to ensure optimal fetal development."
  },
  {
    phase: "03", title: "Delivery Prep", color: "#B8C4E8",
    description: "Childbirth education, energy-focused nutrition, and physical readiness guidance for a confident delivery.",
    details: "Live yoga classes, breathing techniques, and a nutrition strategy designed to build your stamina and confidence for labor and delivery."
  },
  {
    phase: "04", title: "Postnatal", color: "#C4B896",
    description: "Breastfeeding nutrition support, recovery-focused meal plans, and continued guidance as you settle into motherhood.",
    details: "Your body needs careful nourishment to recover and produce quality breast milk. We support you through this transition with dedicated postnatal nutrition plans."
  },
];

const FEATURES = [
  { icon: Sparkles, text: "Everything included in Fitly Elite", description: "The complete 5-pillar precision fertility protocol with 12-week curriculum", highlight: true },
  { icon: Users, text: "Nadiya personally accompanies you", description: "Personal guidance from Nadiya for every doctor consultation and key decision" },
  { icon: PhoneCall, text: "Live counselling sessions", description: "Regular live sessions with Nadiya for emotional support and progress review" },
  { icon: Salad, text: "Personalized fertility & preconception nutrition", description: "Diet plans that evolve across your entire journey from conception to postpartum" },
  { icon: Calendar, text: "Weekly diet updates & monitoring", description: "Continuous feedback, meal monitoring, and adjustments based on your progress" },
  { icon: MessageCircle, text: "Private WhatsApp with 3 nutritionists", description: "Round-the-clock support from a team of three dedicated nutritionists" },
  { icon: Heart, text: "Live Yoga Classes — 5 days a week", description: "Fertility-focused and prenatal yoga sessions to strengthen body and mind" },
  { icon: Stethoscope, text: "Unlimited specialist consultations", description: "As-needed calls with gynaecologist, andrologist, diabetologist, or Ayurveda specialist" },
  { icon: Baby, text: "Prenatal nutrition & delivery prep", description: "Trimester-specific meal plans, childbirth education, and labour readiness" },
  { icon: Apple, text: "Breastfeeding & postnatal support", description: "Nutrition for lactation, recovery, and adjusting to life as a new mother" },
  { icon: ShieldCheck, text: "Dedicated care coordinator", description: "One point of contact who manages your entire journey across all specialists" },
];

export default function FitlyPremiumPlusPage() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });

  const subtotal = PRICE;
  const gst = Math.round(subtotal * GST_RATE);
  const total = subtotal + gst;

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    axios.get(`${API}/razorpay-key`).then((res) => { if (!cancelled && res.data.configured) setRazorpayKey(res.data.key_id); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handlePayment = async (fd) => {
    if (!razorpayKey) return;
    setIsProcessing(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Failed to load payment gateway");
      const orderRes = await axios.post(`${API}/create-order`, {
        amount: subtotal * 100, program_name: "Fitly Premium Plus",
        customer_name: fd.name, customer_email: fd.email, customer_phone: fd.phone,
      });
      const options = {
        key: razorpayKey, amount: orderRes.data.amount, currency: orderRes.data.currency,
        name: "Fitly India", description: "Fitly Premium Plus — Conception to Motherhood",
        order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/verify-payment`, { razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature });
            setPaymentStatus({ type: "success", message: "Payment successful! Our team will contact you within 24 hours." });
          } catch { setPaymentStatus({ type: "error", message: "Payment verification failed. Contact support." }); }
        },
        prefill: { name: fd.name, email: fd.email, contact: fd.phone },
        theme: { color: "#0D1F17" },
        modal: { ondismiss: () => setIsProcessing(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { setPaymentStatus({ type: "error", message: "Payment failed." }); setIsProcessing(false); });
      rzp.open();
    } catch { setPaymentStatus({ type: "error", message: "Could not initiate payment." }); }
    finally { setIsProcessing(false); }
  };

  const inputClass = "w-full bg-[#0A1711] border border-[#F7F5F0]/10 px-4 py-3.5 text-[#F7F5F0] placeholder-[#A3B8AD]/40 focus:border-[#D4AF37]/50 focus:outline-none rounded-xl text-sm";

  return (
    <div className="min-h-screen bg-[#0D1F17] text-[#F7F5F0]">
      {/* Header */}
      <header className="border-b border-[#F7F5F0]/5 bg-[#0D1F17]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#A3B8AD] hover:text-[#F7F5F0] transition-colors" data-testid="back-to-home">
            <ArrowLeft size={18} /><span className="text-sm tracking-wide">Back</span>
          </button>
          <a href="/" className="font-serif text-xl">
            <span className="font-semibold text-[#F7F5F0]">Fitly</span> <span className="font-light text-[#A3B8AD]">India</span>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D1F17] via-[#132A20] to-[#0D1F17]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")" }} />
        <div className="absolute top-20 right-10 w-80 h-80 rounded-full bg-[#E8B4B8]/5 blur-[100px]" />
        <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-[#D4AF37]/5 blur-[100px]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <SectionLabel light>Our Most Comprehensive Program</SectionLabel>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-6 mb-4" data-testid="premium-plus-hero-title">
              Fitly Premium <span className="italic font-light text-[#D4AF37]">Plus</span>
            </h1>
            <p className="text-[#D4AF37]/80 text-xl font-serif italic mb-4">Conception to Motherhood</p>
            <p className="text-[#F7F5F0]/50 text-sm max-w-xl mx-auto leading-relaxed">
              Up to 9 months of dedicated, end-to-end support — from preconception nutrition through pregnancy, delivery, and postnatal recovery.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-12">
            <a href="#enroll" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0D1F17] px-10 py-4 text-sm font-bold tracking-wide rounded-full hover:bg-[#B5952F] transition-all shadow-lg shadow-[#D4AF37]/20" data-testid="hero-enroll-cta">
              Enroll — ₹{formatPrice(PRICE)} <ArrowRight size={16} />
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="mt-14 flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {[
              { value: "9", label: "Months of Support" },
              { value: "6+", label: "Specialists" },
              { value: "5 days/wk", label: "Live Yoga" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-serif text-2xl md:text-3xl text-[#D4AF37]">{s.value}</p>
                <p className="text-xs text-[#A3B8AD]/60 mt-1 tracking-wider uppercase">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Journey — Light section */}
      <section className="py-24 md:py-32 bg-[#FEFDFB] text-[#0D1F17]" data-testid="journey-phases">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <SectionLabel>Your Journey</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D1F17] mt-4">From conception to motherhood</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {JOURNEY.map((phase, i) => (
              <motion.div key={phase.title} {...stagger(i)} data-testid={`journey-phase-${i}`}
                className="relative bg-white border border-[#0D1F17]/8 rounded-2xl p-7 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: phase.color }} />
                <span className="font-serif text-4xl font-bold" style={{ color: `${phase.color}30` }}>{phase.phase}</span>
                <h3 className="font-serif text-xl text-[#0D1F17] mt-2 mb-3">{phase.title}</h3>
                <p className="text-sm text-[#0D1F17]/70 leading-relaxed mb-3">{phase.description}</p>
                <p className="text-xs text-[#0D1F17]/40 leading-relaxed">{phase.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features — Dark */}
      <section className="py-24 md:py-32 bg-[#0D1F17] relative overflow-hidden" data-testid="features-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")" }} />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div {...fadeUp} className="text-center mb-16">
            <SectionLabel light>What's Included</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F7F5F0] mt-4">
              The most comprehensive program we offer
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div key={f.text} {...stagger(i)} data-testid={`feature-${i}`}
                className={`p-6 rounded-xl border transition-all ${
                  f.highlight
                    ? "bg-[#D4AF37]/5 border-[#D4AF37]/20 md:col-span-2"
                    : "bg-[#132A20]/40 border-[#F7F5F0]/5 hover:border-[#F7F5F0]/10"
                }`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    f.highlight ? "bg-[#D4AF37]/15 border border-[#D4AF37]/30" : "bg-[#F7F5F0]/5 border border-[#F7F5F0]/5"
                  }`}>
                    <f.icon size={18} className={f.highlight ? "text-[#D4AF37]" : "text-[#A3B8AD]"} />
                  </div>
                  <div>
                    <h4 className={`text-sm font-semibold ${f.highlight ? "text-[#D4AF37]" : "text-[#F7F5F0]"}`}>{f.text}</h4>
                    <p className="text-xs text-[#A3B8AD] mt-1 leading-relaxed">{f.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enroll — Light */}
      <section id="enroll" className="py-24 md:py-32 bg-[#FEFDFB] text-[#0D1F17]" data-testid="enroll-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <SectionLabel>Begin Your Journey</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D1F17] mt-4">Enroll in Premium Plus</h2>
          </motion.div>

          <div className="max-w-lg mx-auto">
            {/* Price Summary */}
            <motion.div {...fadeUp} className="bg-[#0D1F17] text-[#F7F5F0] rounded-2xl p-8 mb-8 shadow-xl">
              <div className="text-center mb-6">
                <p className="font-serif text-xl text-[#F7F5F0]">Fitly Premium Plus</p>
                <p className="text-xs text-[#A3B8AD] mt-1">Up to 9 months of dedicated support</p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between" data-testid="enroll-subtotal">
                  <span className="text-[#A3B8AD]">Program Fee</span><span className="text-[#F7F5F0]">₹{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between" data-testid="enroll-gst">
                  <span className="text-[#A3B8AD]">GST (18%)</span><span className="text-[#F7F5F0]">₹{formatPrice(gst)}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-[#F7F5F0]/10">
                  <span className="font-serif text-lg text-[#D4AF37]">Total</span>
                  <span className="font-serif text-3xl text-[#F7F5F0]" data-testid="enroll-total">₹{formatPrice(total)}</span>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            {paymentStatus?.type === "success" ? (
              <motion.div {...fadeUp} className="text-center py-12 bg-white border border-[#0D1F17]/8 rounded-2xl shadow-sm">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Check size={28} className="text-[#D4AF37]" />
                </div>
                <p className="text-[#0D1F17] font-serif text-xl">{paymentStatus.message}</p>
              </motion.div>
            ) : (
              <motion.form {...fadeUp} onSubmit={(e) => { e.preventDefault(); handlePayment(formData); }}
                className="bg-white border border-[#0D1F17]/8 rounded-2xl p-8 space-y-4 shadow-sm" data-testid="enrollment-form">
                <div>
                  <label className="block text-xs text-[#0D1F17]/50 mb-1.5 tracking-wider">Full Name *</label>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name" data-testid="enroll-name"
                    className="w-full bg-[#FEFDFB] border border-[#0D1F17]/10 px-4 py-3.5 text-[#0D1F17] placeholder-[#0D1F17]/25 focus:border-[#D4AF37]/50 focus:outline-none rounded-xl text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-[#0D1F17]/50 mb-1.5 tracking-wider">Email *</label>
                  <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com" data-testid="enroll-email"
                    className="w-full bg-[#FEFDFB] border border-[#0D1F17]/10 px-4 py-3.5 text-[#0D1F17] placeholder-[#0D1F17]/25 focus:border-[#D4AF37]/50 focus:outline-none rounded-xl text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-[#0D1F17]/50 mb-1.5 tracking-wider">Phone *</label>
                  <input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Your phone number" data-testid="enroll-phone"
                    className="w-full bg-[#FEFDFB] border border-[#0D1F17]/10 px-4 py-3.5 text-[#0D1F17] placeholder-[#0D1F17]/25 focus:border-[#D4AF37]/50 focus:outline-none rounded-xl text-sm" />
                </div>
                {paymentStatus?.type === "error" && <p className="text-red-500 text-sm">{paymentStatus.message}</p>}
                <button type="submit" disabled={isProcessing} data-testid="enroll-pay-btn"
                  className="w-full bg-[#0D1F17] text-[#F7F5F0] py-4 text-sm font-bold tracking-wide rounded-full hover:bg-[#0D1F17]/90 transition-all disabled:opacity-50 mt-2">
                  {isProcessing ? "Processing..." : `Pay ₹${formatPrice(total)}`}
                </button>
                <p className="text-center text-[#0D1F17]/30 text-xs">Secure payments powered by Razorpay</p>
              </motion.form>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-28 bg-[#0D1F17] text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#132A20]/50 to-transparent" />
        <div className="relative z-10">
          <motion.div {...fadeUp}>
            <Award size={36} className="text-[#D4AF37] mx-auto mb-5" />
            <h2 className="font-serif text-3xl text-[#F7F5F0] mb-3">Have questions?</h2>
            <p className="text-[#A3B8AD] mb-8 max-w-md mx-auto text-sm">
              Book a consultation call with our team before enrolling.
            </p>
            <a href="/consultation" data-testid="cta-book-consultation"
              className="inline-flex items-center gap-2 border border-[#D4AF37]/30 text-[#D4AF37] px-10 py-4 text-sm font-semibold tracking-wide rounded-full hover:bg-[#D4AF37] hover:text-[#0D1F17] transition-all">
              Book Consultation <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
